import { css, useTheme } from '@emotion/react';
import { ENERGY_DEFAULT, ENERGY_MAX, ENERGY_MIN, STYLE_PRESETS } from '@sonematic/shared';

import { fontSizes, spacing } from '@/theme/tokens';
import { AdvancedStylesPanel } from './advanced-styles-panel';
import type { AdvancedStylesPanelProps } from './advanced-styles-panel';

export interface MusicSettingsPanelProps {
  positiveGlobalStyles: string[];
  negativeGlobalStyles: string[];
  onPositiveGlobalStylesChange: (styles: string[]) => void;
  onNegativeGlobalStylesChange: (styles: string[]) => void;
  energy: number | null;
  onEnergyChange: (energy: number) => void;
  advancedMode?: boolean;
  onToggleAdvanced?: () => void;
  advancedStylesProps?: AdvancedStylesPanelProps;
}

const BPM_PATTERN = /^\d+ BPM$/;

/** Look up a style string in STYLE_PRESETS to get a human-readable label. */
function styleLabel(styleString: string): string {
  const preset = STYLE_PRESETS.find((p) => p.styleString === styleString);
  return preset ? preset.label : styleString;
}

export function MusicSettingsPanel({
  positiveGlobalStyles,
  negativeGlobalStyles,
  onPositiveGlobalStylesChange,
  onNegativeGlobalStylesChange,
  energy,
  onEnergyChange,
  advancedMode,
  onToggleAdvanced,
  advancedStylesProps,
}: MusicSettingsPanelProps) {
  const theme = useTheme();
  const effectiveEnergy = energy ?? ENERGY_DEFAULT;

  const handleRemovePositive = (style: string) => {
    onPositiveGlobalStylesChange(positiveGlobalStyles.filter((s) => s !== style));
  };

  const handleRemoveNegative = (style: string) => {
    onNegativeGlobalStylesChange(negativeGlobalStyles.filter((s) => s !== style));
  };

  const chipStyles = css`
    padding: ${spacing.xs} ${spacing.md};
    border-radius: 16px;
    border: 1px solid ${theme.colors.border};
    background: ${theme.colors.text};
    color: ${theme.colors.surface};
    font-size: ${fontSizes.sm};
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover {
      opacity: 0.8;
    }
  `;

  const sectionHeadingStyles = css`
    font-size: ${fontSizes.sm};
    color: ${theme.colors.textSecondary};
    font-weight: 500;
    display: block;
    margin-bottom: ${spacing.sm};
  `;

  const chipGroupStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing.sm};
  `;

  const toggleButtonStyles = css`
    background: none;
    border: none;
    color: ${theme.colors.textSecondary};
    font-size: ${fontSizes.xs};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 4px;
  `;

  if (advancedMode && advancedStylesProps) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.md};
        `}
      >
        <AdvancedStylesPanel {...advancedStylesProps} />
        {onToggleAdvanced !== undefined && (
          <button type="button" css={toggleButtonStyles} onClick={onToggleAdvanced}>
            Hide Advanced Settings
          </button>
        )}
      </div>
    );
  }

  const displayPositiveStyles = positiveGlobalStyles.filter((s) => !BPM_PATTERN.test(s));
  const displayNegativeStyles = negativeGlobalStyles.filter((s) => !BPM_PATTERN.test(s));

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.md};
      `}
    >
      {/* Positive styles (music style) */}
      <div>
        <span css={sectionHeadingStyles}>Music Style</span>
        <div css={chipGroupStyles}>
          {displayPositiveStyles.length === 0 && (
            <span
              css={css`
                font-size: ${fontSizes.sm};
                color: ${theme.colors.textSecondary};
                font-style: italic;
              `}
            >
              No styles selected
            </span>
          )}
          {displayPositiveStyles.map((style) => (
            <button
              key={style}
              type="button"
              onClick={() => handleRemovePositive(style)}
              css={chipStyles}
              aria-label={`Remove ${styleLabel(style)}`}
            >
              {styleLabel(style)}
              <span aria-hidden="true">&times;</span>
            </button>
          ))}
        </div>
      </div>

      {/* Energy slider */}
      <div>
        <span css={sectionHeadingStyles}>Energy</span>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.sm};
          `}
        >
          <span
            css={css`
              font-size: ${fontSizes.xs};
              color: ${theme.colors.textSecondary};
            `}
          >
            Calm
          </span>
          <input
            type="range"
            min={ENERGY_MIN}
            max={ENERGY_MAX}
            value={effectiveEnergy}
            aria-label="Energy level"
            onChange={(e) => onEnergyChange(Number(e.target.value))}
            css={css`
              flex: 1;
              accent-color: ${theme.colors.primary};
            `}
          />
          <span
            css={css`
              font-size: ${fontSizes.xs};
              color: ${theme.colors.textSecondary};
            `}
          >
            Intense
          </span>
          <span
            css={css`
              font-size: ${fontSizes.sm};
              color: ${theme.colors.text};
              font-weight: 500;
              min-width: 20px;
              text-align: center;
            `}
          >
            {effectiveEnergy}
          </span>
        </div>
      </div>

      {/* Negative styles */}
      {displayNegativeStyles.length > 0 && (
        <div>
          <span css={sectionHeadingStyles}>Negative Styles</span>
          <div css={chipGroupStyles}>
            {displayNegativeStyles.map((style) => (
              <button
                key={style}
                type="button"
                onClick={() => handleRemoveNegative(style)}
                css={chipStyles}
                aria-label={`Remove negative style ${styleLabel(style)}`}
              >
                {styleLabel(style)}
                <span aria-hidden="true">&times;</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {advancedMode !== undefined && onToggleAdvanced !== undefined && (
        <>
          <div
            css={css`
              border-top: 1px solid ${theme.colors.border};
              margin-top: ${spacing.xs};
            `}
          />
          <button type="button" css={toggleButtonStyles} onClick={onToggleAdvanced}>
            Show Advanced Settings
          </button>
        </>
      )}
    </div>
  );
}
